/**
 * Post-purchase welcome page (`/welcome?ty=…`).
 *
 * WordPress hands a new membership sign-up straight to this route instead of
 * rendering a thank-you page (see JosephineThankYou.php in openstudio-core).
 * The `ty` query param is an unpadded base64url JSON payload describing the
 * order — display data only; the session comes from the SSO token and the
 * subscription itself arrives through user_subscription_state.
 */

export type WelcomeTier = 'regular' | 'pro';

export type WelcomeItem = {
  id: number;
  name: string;
  qty: number;
  total: number;
  kind: 'membership' | 'course' | 'other';
};

export type WelcomeConversionItem = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
};

/**
 * One `os_order_complete` dataLayer event, the shape WordPress thank-you
 * pages push for Google Tag Manager (ConversionTracking.php in
 * openstudio-core). Pushed as-is; the buyer's email is added from the session.
 */
export type WelcomeConversion = {
  event: 'os_order_complete';
  order_id: number;
  /** Order ID as a string: Google Ads dedupes on it. */
  transaction_id: string;
  /** Charged today. */
  value: number;
  currency: string;
  order_type: 'purchase' | 'trial_start' | 'free' | 'switch' | 'renewal';
  trial_start: boolean;
  tier: string;
  plan: string;
  /** Recurring amount of the subscription created (0 when none). */
  plan_value: number;
  trial_end: string;
  upsell: boolean;
  primary_order_id: number;
  new_customer: boolean;
  coupons: string;
  items: WelcomeConversionItem[];
  /** GA4-shaped copy for GTM variables written against the WordPress data layer. */
  ecommerce: { transaction_id: string; value: number; currency: string; items: WelcomeConversionItem[] };
};

export type WelcomePayload = {
  v: number;
  order: number;
  number: string;
  first: string;
  tier: WelcomeTier;
  currency: string;
  /** Charged today (0 for a free trial). */
  total: number;
  items: WelcomeItem[];
  /** VWO `trialStarted` plans to fire: 'monthly' | 'annual'. */
  vwo: string[];
  /** Membership line item name, e.g. "Open Studio - Monthly". */
  plan: string;
  /** WooCommerce billing period: day | week | month | year. */
  period: string;
  interval: number;
  /** Recurring amount per period. */
  recurring: number;
  trial: boolean;
  /** YYYY-MM-DD in the site timezone, '' when not applicable. */
  trialEnd: string;
  nextPayment: string;
  /** v2: conversion events to push, one per paid order of the purchase (primary first). */
  conversions: WelcomeConversion[];
};

function base64UrlDecode(input: string): string | null {
  try {
    const padded = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const binary = globalThis.atob(padded + pad);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function itemKind(v: unknown): WelcomeItem['kind'] {
  return v === 'membership' || v === 'course' ? v : 'other';
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function orderType(v: unknown): WelcomeConversion['order_type'] {
  return v === 'purchase' || v === 'trial_start' || v === 'free' || v === 'switch' || v === 'renewal' ? v : 'purchase';
}

function conversionItems(v: unknown): WelcomeConversionItem[] {
  if (!Array.isArray(v)) return [];
  return v.filter(isRecord).map((it) => ({
    item_id: str(it.item_id, String(num(it.item_id))),
    item_name: str(it.item_name),
    price: num(it.price),
    quantity: Math.max(1, num(it.quantity, 1)),
  }));
}

function decodeConversion(v: unknown): WelcomeConversion | null {
  if (!isRecord(v)) return null;
  const orderId = num(v.order_id);
  if (!orderId) return null;

  const transactionId = str(v.transaction_id, String(orderId)) || String(orderId);
  const value = num(v.value);
  const currency = str(v.currency, 'USD').toUpperCase() || 'USD';
  const items = conversionItems(v.items);

  return {
    event: 'os_order_complete',
    order_id: orderId,
    transaction_id: transactionId,
    value,
    currency,
    order_type: orderType(v.order_type),
    trial_start: v.trial_start === true,
    tier: str(v.tier),
    plan: str(v.plan),
    plan_value: num(v.plan_value),
    trial_end: str(v.trial_end),
    upsell: v.upsell === true,
    primary_order_id: num(v.primary_order_id),
    new_customer: v.new_customer === true,
    coupons: str(v.coupons),
    items,
    ecommerce: { transaction_id: transactionId, value, currency, items },
  };
}

/**
 * A v1 payload (no `conv`) still gets one event for its order so a link
 * minted before the WordPress deploy is not lost. new_customer is unknown
 * there and assumed true: v1 links were only ever minted for new sign-ups.
 */
function conversionFromV1(p: Omit<WelcomePayload, 'conversions'>): WelcomeConversion {
  const items = p.items.map((item) => ({
    item_id: String(item.id),
    item_name: item.name,
    price: item.qty ? Math.round((item.total / item.qty) * 100) / 100 : item.total,
    quantity: item.qty,
  }));
  const plan =
    p.interval === 1 && p.period === 'month'
      ? 'monthly'
      : p.interval === 1 && p.period === 'year'
        ? 'annual'
        : p.period
          ? `${p.interval}-${p.period}`
          : '';

  return {
    event: 'os_order_complete',
    order_id: p.order,
    transaction_id: String(p.order),
    value: p.total,
    currency: p.currency,
    order_type: p.total > 0 ? 'purchase' : p.trial ? 'trial_start' : 'free',
    trial_start: p.trial,
    tier: p.tier,
    plan,
    plan_value: p.recurring,
    trial_end: p.trialEnd,
    upsell: false,
    primary_order_id: 0,
    new_customer: true,
    coupons: '',
    items,
    ecommerce: { transaction_id: String(p.order), value: p.total, currency: p.currency, items },
  };
}

/** Parses the `ty` query param; null when missing or malformed. */
export function decodeWelcomePayload(raw: unknown): WelcomePayload | null {
  const encoded = Array.isArray(raw) ? raw[0] : raw;
  if (typeof encoded !== 'string' || !encoded) return null;

  const json = base64UrlDecode(encoded);
  if (!json) return null;

  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;

  const order = num(d.order);
  const tier = d.tier === 'pro' ? 'pro' : 'regular';
  if (!order) return null;

  const items: WelcomeItem[] = Array.isArray(d.items)
    ? d.items
        .filter((it): it is Record<string, unknown> => !!it && typeof it === 'object')
        .map((it) => ({
          id: num(it.id),
          name: str(it.name),
          qty: Math.max(1, num(it.qty, 1)),
          total: num(it.total),
          kind: itemKind(it.kind),
        }))
        .filter((it) => it.name)
    : [];

  const base: Omit<WelcomePayload, 'conversions'> = {
    v: num(d.v, 1),
    order,
    number: str(d.number, String(order)),
    first: str(d.first).trim(),
    tier,
    currency: str(d.currency, 'USD').toUpperCase() || 'USD',
    total: num(d.total),
    items,
    vwo: Array.isArray(d.vwo) ? d.vwo.filter((p): p is string => typeof p === 'string') : [],
    plan: str(d.plan),
    period: str(d.period, 'month'),
    interval: Math.max(1, num(d.interval, 1)),
    recurring: num(d.recurring),
    trial: d.trial === true,
    trialEnd: str(d.trial_end),
    nextPayment: str(d.next_payment),
  };

  const conversions = Array.isArray(d.conv)
    ? d.conv.map(decodeConversion).filter((c): c is WelcomeConversion => c !== null)
    : [conversionFromV1(base)];

  return { ...base, conversions };
}

function parseYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "YYYY-MM-DD" → "October 27, 2026" (site-local calendar date, no TZ shift). */
export function formatWelcomeDate(ymd: string): string {
  const date = parseYmd(ymd);
  if (!date) return ymd;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** "YYYY-MM-DD" → "Oct 27, 2026" (billing timeline labels). */
export function formatWelcomeDateShort(ymd: string): string {
  const date = parseYmd(ymd);
  if (!date) return ymd;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "YYYY-MM-DD" → "October 27" (the year is already on the timeline label above). */
export function formatWelcomeMonthDay(ymd: string): string {
  const date = parseYmd(ymd);
  if (!date) return ymd;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/** Billing cadence pill: "Annual", "Monthly", "Every 3 months". */
export function cadenceLabel(period: string, interval: number): string {
  const unit = period || 'month';
  if (interval > 1) return `Every ${interval} ${unit}s`;
  if (unit === 'year') return 'Annual';
  if (unit === 'month') return 'Monthly';
  if (unit === 'week') return 'Weekly';
  return unit.charAt(0).toUpperCase() + unit.slice(1);
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** "/month", "/year", "/3 months". */
export function periodSuffix(period: string, interval: number): string {
  const unit = period || 'month';
  return interval > 1 ? `/${interval} ${unit}s` : `/${unit}`;
}

export type BillingSummary = {
  /** Eyebrow above the title. */
  eyebrow: string;
  /** One line under the plan pill. */
  line: string;
  /** Highlight bullet about access / trial. */
  accessHighlight: string;
};

export function billingSummary(p: WelcomePayload): BillingSummary {
  const price = p.recurring > 0 ? `${formatMoney(p.recurring, p.currency)}${periodSuffix(p.period, p.interval)}` : '';

  if (p.trial && p.trialEnd) {
    const end = formatWelcomeDate(p.trialEnd);
    return {
      eyebrow: 'Free trial started',
      line: price ? `Free through ${end}, then ${price}` : `Free through ${end}`,
      accessHighlight: `Your free trial gives you unlimited access through ${end}. We'll email you a reminder before it ends.`,
    };
  }

  const next = p.nextPayment ? formatWelcomeDate(p.nextPayment) : '';
  return {
    eyebrow: 'Membership started',
    line: next && price ? `Renews ${next} at ${price}` : price || (next ? `Renews ${next}` : ''),
    accessHighlight:
      p.tier === 'pro'
        ? 'Your membership gives you unlimited access to the entire Open Studio course library, plus 20+ live classes every week.'
        : 'Your membership gives you unlimited access to the entire Open Studio course library.',
  };
}
